// M004-F0757 (WebUI #200): Apply to account. Moves a credit note's unused amount, whole or part, to the customer's on-account credit.
// Spec core-ledger-account-gateway 1.19.0+: credit.allocate_to_account, body { credit_note_id, amount_minor, comment } (additionalProperties false).
// Since 1.19.0 the comment is REQUIRED on the wire (non-blank, no leading/trailing whitespace, at most 500 characters): it is the note on the statement line.
import { remainingMinor, isDraft, isArchivedRow } from './creditNoteModel';
import { money } from '../invoices/invoiceModel';

// Whole-pence amount from the typed pounds value; NaN-safe (anything not a number becomes NaN, and NaN fails every check).
export const COMMENT_MAX = 500;

export const amountMinorOf = (amount) => Math.round(Number(String(amount ?? '').trim()) * 100);

export function validateApplyToAccount({ amount, comment }, { availableMinor }) {
  const errors = {};
  const text = String(amount ?? '').trim();
  const pounds = Number(text);
  const minor = amountMinorOf(text);
  if (!text || !Number.isFinite(pounds) || Math.abs(pounds * 100 - minor) > 1e-6 || minor < 1) {
    errors.amount = 'Enter an amount greater than zero, in whole pence.';
  } else if (minor > Math.max(0, Number(availableMinor) || 0)) {
    errors.amount = 'The amount is more than the credit note has left.';
  }
  if (!String(comment || '').trim()) errors.comment = 'Give a comment for this action.';
  else if (String(comment).trim().length > COMMENT_MAX) errors.comment = `Keep the comment to ${COMMENT_MAX} characters or fewer.`;
  return errors;
}

// The wire body, exactly the three Spec fields. The comment is trimmed: the Spec pattern forbids leading and trailing whitespace.
export function buildApplyToAccountBody({ creditNoteId, amount, comment }) {
  return { credit_note_id: creditNoteId, amount_minor: amountMinorOf(amount), comment: String(comment ?? '').trim() };
}

// The confirm step (ticket line, ML001-D013 §2.72).
export const confirmText = (amountMinor) => `CN available falls by ${money(amountMinor, 'GBP')}; on-account credit rises by ${money(amountMinor, 'GBP')}.`;

// The success statement (ticket line; the statement line text).
export const successText = (creditNoteNumber) => `Credit note ${creditNoteNumber} applied to account`;

// The Ledger's figures after the action, from its response data.
export const figuresText = (data) => `CN available ${money(data?.credit_note_available_minor, 'GBP')}; on-account credit ${money(data?.on_account_credit_minor, 'GBP')}.`;

// A refusal is shown as the Ledger names it: CODE: message, verbatim. No friendly mapping.
export function refusalText(cause, fallback = 'The credit could not be applied to the account.') {
  const data = cause?.response?.data;
  const code = data?.error?.code;
  const message = data?.error?.message || data?.message || cause?.message || fallback;
  return code ? `${code}: ${message}` : message;
}

// Which credit-note rows offer the action: an issued, not archived note that still has unused credit. Own-account is NOT a reason
// to hide it (maker-checker is the Ledger's refusal, shown verbatim).
export const canApplyToAccountRow = (row) => !isDraft(row) && !isArchivedRow(row) && remainingMinor(row) > 0;

// Fresh key per attempt; a changed body starts a new attempt (same rule as the Apply drawer).
export const newIdempotencyKey = () => globalThis.crypto?.randomUUID?.() || `cn-${Date.now()}-${Math.random()}`;
